"use client";
import { Carousel } from "antd";
import Image from "next/image";
import { useMemo } from "react";
import { useIntl } from "react-intl";
import { ICMSHomeBannerTypes } from "../../../Orchestrator/Types/Interface/ICmsHomePageBanner";
import { useRecState } from "../../../Orchestrator/Recoils/Events/Recoil";
import { useOnLanguageChange } from "../../CustomHooks/useOnLanguageChange";
import { _eventBus } from "../../../core/events/EventBus";
import { evtTree } from "../../../Orchestrator/ConstantTree";
import { SearchStateType } from "../../../Orchestrator/Types/Index";
import HeroSkeleton from "./HeroSkeleton";

interface ISliderItem {
  id: number;
  image: string;
  title?: string;
  origin?: { name: string; code?: string };
  destination?: { name: string; code?: string };
}

const ImageSlider = () => {
  const [bannerList] = useRecState<ICMSHomeBannerTypes[] | null>(
    "bannerListResponseAtom"
  );
  const [searchState] = useRecState<SearchStateType>("searchStateAtom");
  useOnLanguageChange(() => {
    _eventBus.emit({
      eventName: evtTree.Banner.List.Get,
    });
  });
  const intl = useIntl();
  const isArabic = intl.locale?.toLowerCase().startsWith("ar");

  // Banner content is fully CMS-driven. Only banners that carry a usable image
  // are rendered; when the CMS returns nothing, the carousel renders nothing.
  const carouselItems: ISliderItem[] = useMemo(
    () =>
      (bannerList ?? [])
        .filter((item) => Boolean(item.image?.url))
        .map((item) => ({
          id: item.id,
          image: item.image.url,
          title: item.title?.trim() || undefined,
          origin: item.origin
            ? { name: item.origin.name, code: item.origin.code }
            : undefined,
          destination: item.destination
            ? { name: item.destination.name, code: item.destination.code }
            : undefined,
        })),
    [bannerList]
  );

  const carouselSettings = useMemo(
    () => ({
      autoplay: true,
      dots: true,
      infinite: true,
      speed: 500,
      slidesToShow: 1,
      slidesToScroll: 1,
    }),
    []
  );

  const handleBookNow = (
    origin: ISliderItem["origin"],
    destination: ISliderItem["destination"]
  ) => {
    _eventBus.emit({
      eventName: evtTree.Banner.BookNow.Click,
      data: {
        search: searchState,
        origin: origin,
        destination: destination,
      },
    });
  };

  const renderRouteText = (item: ISliderItem) => {
    const originName = item.origin?.name ?? "";
    const destinationName = item.destination?.name ?? "";

    return (
      <span
        className="banner_route_text"
        dir={isArabic ? "rtl" : "ltr"}
      >
        {intl.formatMessage({ id: "roundTripBetweenText" })}{" "}
        <strong>
          <bdi dir="auto">{originName}</bdi>
        </strong>
        <span className="banner_route_separator">
          {isArabic ? " ‏-‏ " : " - "}
        </span>
        <strong>
          <bdi dir="auto">{destinationName}</bdi>
        </strong>
      </span>
    );
  };

  if (bannerList === null) {
    return <HeroSkeleton />;
  }

  if (!carouselItems.length) {
    return null;
  }

  return (
    <div className="main_banner">
      <Carousel className="carousel" {...carouselSettings}>
        {carouselItems.map((item, index) => {
          const hasRoute = Boolean(item.origin && item.destination);

          return (
            <div className="slide_item" key={item.id}>
              <div className="slide_img">
                <Image
                  src={item.image}
                  alt={`Banner for ${item.origin?.name ?? ""} - ${item.destination?.name ?? ""
                    }`}
                  width={1195}
                  height={325}
                  style={{ objectFit: "cover" }}
                  // First slide is the LCP element: `priority` makes next/image
                  // load it eagerly with fetchpriority=high. The accurate `sizes`
                  // lets the browser pick the right srcset candidate (improves LCP).
                  priority={index === 0}
                  sizes="(max-width: 768px) 100vw, 1195px"
                />
              </div>
              <div className="banner_content d-flex flex-col gap-24">
                {(item.title || hasRoute) && (
                  <h2>
                    {item.title && <strong>{item.title}</strong>}
                    {hasRoute && (
                      <>
                        {item.title && <br />}
                        {renderRouteText(item)}
                      </>
                    )}
                  </h2>
                )}
                {hasRoute && (
                  <div>
                    <button
                      className="btn"
                      onClick={() => handleBookNow(item.origin, item.destination)}
                    >
                      {intl.formatMessage({ id: "bookNowText" })}
                    </button>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </Carousel>
    </div>
  );
};

export default ImageSlider;
